import { Box, IconButton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import styled from '@emotion/styled';
import { useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useAtomValue } from 'jotai';
import { ReactComponent as ChevronRightIcon } from '../../assets/icons/chevron-right.svg';
import { ROUTES } from '../../constants/routes';
import { useTimesheets } from '../../queries/hooks/useTimesheets';
import { isSmallScreenState, userState } from '../../state/UIState';
import { TimesheetStatus } from '../../types';
import { isSubmitter } from '../../utils/roles';
import { PageHeader } from '../layout/PageHeader';
import { ContentWrapper } from '../layout/styled';
import { TableSkeletonLoader } from '../../components/SkeletonLoader/Table.SkeletonLoader';
import { getMonthLabel } from '../../utils/formatters';
import { TimesheetRouteIdProvider } from '../SingleTimesheet/context/TimesheetRouteIdContext';
import { SingleTimesheetTable } from '../SingleTimesheet/components/SingleTimesheetTable/SingleTimesheetTable';
import {
  PendingMonthViewProvider,
  timesheetWeekStartsInCalendarMonth,
} from './PendingMonthViewContext';
import {
  compareTimesheetsByWeekStart,
  pendingMonthTimesheetDomId,
} from '../../utils/pendingTimesheetsMonthWeeks';
import { scrollToPendingMonthTimesheetAnchor } from '../../utils/scrollToPendingMonthTimesheetAnchor';
import { Typography } from '../../components/Typography/Typography';
import { UserAvatar } from '../../components/UserAvatar/UserAvatar';

function parseYearMonth(params: { year?: string; month?: string }): { year: number; month: number } | null {
  const year = Number(params.year);
  const month = Number(params.month);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return null;
  }
  return { year, month };
}

function shiftCalendarMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

/** Compact “TS Issued” pills (design: ~20px capsule, minimal padding, subtle fill). */
function MonthSummaryPill({ label }: { label: string }) {
  const { colors } = useTheme();
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        px: '6px',
        borderRadius: 999,
        bgcolor: alpha(colors.text.main, 0.06),
        color: colors.text.secondary,
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: '0.02em',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

export function PendingTimesheetsMonthDetail() {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const user = useAtomValue(userState);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const ym = useMemo(() => parseYearMonth(params), [params]);
  const isOP = isSubmitter(user);

  const { isLoading, data: timesheets } = useTimesheets({ userId: user?.id });

  const issuedTimesheets = useMemo(() => {
    if (!timesheets) return [];
    if (isOP)
      return timesheets.filter(
        (ts) => ts.status === TimesheetStatus.ISSUED || ts.status === TimesheetStatus.REJECTED
      );
    return timesheets.filter((ts) => ts.status === TimesheetStatus.ISSUED);
  }, [timesheets, isOP]);

  const { issuedCount, rejectedCount, submittedCount } = useMemo(() => {
    let issued = 0;
    let rejected = 0;
    if (ym) {
      for (const ts of issuedTimesheets) {
        if (!timesheetWeekStartsInCalendarMonth(ts, ym.year, ym.month)) continue;
        if (ts.status === TimesheetStatus.REJECTED) rejected += 1;
        else if (ts.status === TimesheetStatus.ISSUED) issued += 1;
      }
    }
    let submitted = 0;
    if (timesheets && ym) {
      for (const ts of timesheets) {
        if (!timesheetWeekStartsInCalendarMonth(ts, ym.year, ym.month)) continue;
        if (
          ts.status === TimesheetStatus.PENDING_ACCOUNTANT ||
          ts.status === TimesheetStatus.PENDING_OP_MANAGER ||
          ts.status === TimesheetStatus.PENDING_DEAL_OWNERS ||
          ts.status === TimesheetStatus.CLOSED
        ) {
          submitted += 1;
        }
      }
    }
    return { issuedCount: issued, rejectedCount: rejected, submittedCount: submitted };
  }, [issuedTimesheets, timesheets, ym]);

  /** Issued, rejected (OP), and submitted weeks in a single calendar order. */
  const monthViewWeeksOrdered = useMemo(() => {
    if (!ym || !timesheets) return [];
    return timesheets
      .filter((ts) => timesheetWeekStartsInCalendarMonth(ts, ym.year, ym.month))
      .filter((ts) => {
        const submitted =
          ts.status === TimesheetStatus.PENDING_ACCOUNTANT ||
          ts.status === TimesheetStatus.PENDING_OP_MANAGER ||
          ts.status === TimesheetStatus.PENDING_DEAL_OWNERS ||
          ts.status === TimesheetStatus.CLOSED;
        const editable =
          ts.status === TimesheetStatus.ISSUED || (isOP && ts.status === TimesheetStatus.REJECTED);
        return editable || submitted;
      })
      .sort(compareTimesheetsByWeekStart)
      .reverse();
  }, [timesheets, ym, isOP]);

  /** Anchor id from `#pending-month-ts-…` (used for scroll + left accent). */
  const hashFocusAnchorId = useMemo(() => {
    const raw = location.hash;
    if (!raw || raw.length < 2) return '';
    try {
      return decodeURIComponent(raw.slice(1));
    } catch {
      return raw.slice(1);
    }
  }, [location.hash]);

  // Tracks the anchor we've already auto-scrolled to. Without this, the effect re-fires whenever
  // `monthViewWeeksOrdered` is recomputed (e.g. after submitting a timesheet triggers a refetch),
  // which would yank the page back down to the anchor on every submit.
  const scrolledAnchorRef = useRef<string>('');

  useEffect(() => {
    if (!hashFocusAnchorId) {
      scrolledAnchorRef.current = '';
      return;
    }
    if (monthViewWeeksOrdered.length === 0) return;
    if (scrolledAnchorRef.current === hashFocusAnchorId) return;
    scrolledAnchorRef.current = hashFocusAnchorId;
    return scrollToPendingMonthTimesheetAnchor(hashFocusAnchorId, {
      expectedWeekCount: monthViewWeeksOrdered.length,
    });
  }, [hashFocusAnchorId, monthViewWeeksOrdered]);

  useEffect(() => {
    if (!ym) {
      navigate(`/${ROUTES.PENDING_TIMESHEETS}`, { replace: true });
    }
  }, [ym, navigate]);

  const pageTitle = ym ? `${getMonthLabel(ym.month)} ${ym.year}` : '';

  const prevYm = ym ? shiftCalendarMonth(ym.year, ym.month, -1) : null;
  const nextYm = ym ? shiftCalendarMonth(ym.year, ym.month, 1) : null;

  const titleEndAdornment = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
      {issuedCount > 0 && <MonthSummaryPill label={`${issuedCount} TS Issued`} />}
      {rejectedCount > 0 && <MonthSummaryPill label={`${rejectedCount} TS Sent Back`} />}
      {submittedCount > 0 && <MonthSummaryPill label={`${submittedCount} TS Submitted`} />}
    </Box>
  );

  const iconButtonNavSx = {
    svg: { path: { fill: colors.icon.accent } },
    backgroundColor: colors.surfaceBackground.highlighted,
    borderRadius: '4px',
    ':hover': {
      backgroundColor: colors.surfaceBackground.highlighted,
    },
  } as const;

  const monthNavControls =
    prevYm && nextYm ? (
      <MonthPaginationWrap isSmallScreen={isSmallScreen}>
        <IconButton
          aria-label='Previous month'
          onClick={() =>
            navigate(`/${ROUTES.PENDING_TIMESHEETS}/month/${prevYm.year}/${prevYm.month}`)
          }
          sx={iconButtonNavSx}
        >
          <StyledMonthChevronPrev />
        </IconButton>
        <IconButton
          aria-label='Next month'
          onClick={() =>
            navigate(`/${ROUTES.PENDING_TIMESHEETS}/month/${nextYm.year}/${nextYm.month}`)
          }
          sx={iconButtonNavSx}
        >
          <StyledMonthChevronNext />
        </IconButton>
        <Typography
          variant='body'
          color={colors.text.main}
          style={{
            lineHeight: 'inherit',
            fontWeight: 600,
            fontSize: 15,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
          }}
        >
          {pageTitle}
        </Typography>
      </MonthPaginationWrap>
    ) : null;

  const belowTitle =
    monthNavControls || user?.name ? (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          width: '100%',
          marginTop: '4px',
          columnGap: 1,
        }}
      >
        <Box sx={{ justifySelf: 'start', minWidth: 0, display: 'flex', alignItems: 'center' }}>
          {user?.name ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <UserAvatar
                name={user.name}
                size='medium'
                style={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                }}
                lettersStyle={{
                  color: colors.icon.inverse,
                  fontSize: 13,
                  lineHeight: '34px',
                }}
              />
              <Typography variant='body' color={colors.text.secondary} style={{ fontSize: '14px' }}>
                {user.name}
                {isOP ? ' (You)' : ''}
              </Typography>
            </Box>
          ) : null}
        </Box>
        <Box sx={{ justifySelf: 'center' }}>{monthNavControls}</Box>
        <Box sx={{ minWidth: 0 }} aria-hidden />
      </Box>
    ) : null;

  if (!ym) return null;

  if (isLoading || !timesheets) return <TableSkeletonLoader />;

  return (
    <>
      <PageHeader
        title={pageTitle}
        description=''
        breadcrumbs={[
          {
            title: 'All timesheet',
            url: `/${ROUTES.ALL_TIMESHEETS}`,
            color: colors.textAccent.default,
          },
          {
            title: 'Pending Timesheets',
            url: `/${ROUTES.PENDING_TIMESHEETS}`,
            color: colors.textAccent.default,
          },
          { title: pageTitle },
        ]}
        titleEndAdornment={titleEndAdornment}
        belowTitle={belowTitle}
      />
      <ContentWrapper isSmallScreen={isSmallScreen}>
        <PendingMonthViewProvider
          viewedYear={ym.year}
          viewedMonth={ym.month}
          allTimesheets={timesheets}
        >
          {monthViewWeeksOrdered.length === 0 ? (
            <Typography variant='body' color={colors.text.secondary} style={{ paddingTop: 16 }}>
              No timesheets for this month.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {monthViewWeeksOrdered.map((ts) => {
                const readOnlySubmitted =
                  ts.status === TimesheetStatus.PENDING_ACCOUNTANT ||
                  ts.status === TimesheetStatus.PENDING_OP_MANAGER ||
                  ts.status === TimesheetStatus.PENDING_DEAL_OWNERS ||
                  ts.status === TimesheetStatus.CLOSED;
                const weekAnchorId = pendingMonthTimesheetDomId(ts.id);
                const isWeekHashFocused = hashFocusAnchorId === weekAnchorId;
                return (
                  <Box
                    key={ts.id}
                    id={weekAnchorId}
                    sx={{
                      borderRadius: 1,
                      scrollMarginTop: 24,
                    }}
                  >
                    <TimesheetRouteIdProvider timesheetId={String(ts.id)}>
                      <SingleTimesheetTable
                        embeddedInMonthView
                        embeddedWeekAccentStripe={isWeekHashFocused}
                        readOnlySubmitted={readOnlySubmitted}
                        monthListTimesheet={readOnlySubmitted ? ts : undefined}
                      />
                    </TimesheetRouteIdProvider>
                  </Box>
                );
              })}
            </Box>
          )}
        </PendingMonthViewProvider>
      </ContentWrapper>
    </>
  );
}

const MonthPaginationWrap = styled('div')<{ isSmallScreen: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  padding: ${({ isSmallScreen }) => (isSmallScreen ? '4px 0 0' : '2px 0 0')};
`;

const StyledMonthChevronPrev = styled(ChevronRightIcon)`
  transform: rotate(180deg);
`;

const StyledMonthChevronNext = styled(ChevronRightIcon)``;
